// Furniture white-glove delivery & install close-out proof steps — storyline v2.
//
// Five checkpoints for a "Hartley" 6-drawer dresser delivery (fictional unbranded SKU), merged
// from the cofounder's demo storyline (docs/superpowers/notes/2026-07-17-cofounder-storyline.md)
// and the KB-grounded v1 sequence: item at vehicle (seeded carton flag) → condition walk-around
// at the threshold (clears the flag) → carcase level check → anti-tip anchor (the fail beat) →
// placement & function. The v1 feet / drawers / handoff checkpoints are hidden for now (assets
// remain in the repo); the condition multi-capture became a single walk-around clip.
//
// Every checkpoint is a VIDEO shot (auto-captures after the AR lock — no shutter tap). The
// vehicle clip is synthesized from its still (gen_handheld_still.py) with a scanning→orange
// `flag` colorShift burned in: the crush is documented, not passed-green or failed-red.
//
// The signature beat is the ANCHOR step (4): the first capture fails (strap fastened to the
// unit but never attached to the wall) → routes to verify-failed → the retake passes. See ANCHOR_FAIL below and the
// `anchor` guards in CaptureScreen.tsx / store.tsx.
import type { BoxTrack } from './boxTrack.js'
import vehicleTrack from './boxTracks/vehicle.json'
import conditionTrack from './boxTracks/condition.json'
import levelTrack from './boxTracks/level.json'
import anchorTrack from './boxTracks/anchor.json'
import anchorFailTrack from './boxTracks/anchor-fail.json'
import placementTrack from './boxTracks/placement.json'

export type ProofStepId = 'vehicle' | 'condition' | 'level' | 'anchor' | 'placement'

export interface DetectionBoxSpec {
  top: string
  left: string
  width: string
  height: string
  text: string
  // 'flag' renders the locked/pass box in ORANGE — a documented condition worth attention
  // (not a pass-green, not a fail-red). Used by the vehicle carton-crush checkpoint.
  tone?: 'flag'
}

// A faked-OCR two-reading comparison shown on the verify-passed screen (unused in this app;
// kept for template parity with the HVAC base).
export interface CompareSpec {
  caption: string
  left: { thumb: string; label: string; value: string }
  right: { thumb: string; label: string; value: string }
  verdict: string
}

// A multi-capture checkpoint: several clips captured back-to-back, then a summary screen
// showing all their posters. Unused in storyline v2 (the condition scan became one walk-around
// clip); the mechanism stays for future checkpoints.
export interface SubShot {
  id: string
  video: BoxTrack
  summaryLabel: string
}

export interface ProofStep {
  id: ProofStepId
  num: string
  label: string
  basePhoto: string
  // The primer ("Ready to capture") card's instructional GUIDE image: a clean 3:2
  // landscape framing example with NO baked text/box (asset-regen/gen_primers.py).
  // The app draws the dashed framing guide (primerBox) + caption on top at runtime.
  primerImage: string
  // When present, the viewfinder plays this clip with a burned-in AR box and the verify
  // screen shows its poster. Absent → still-image shot (basePhoto + runtime cameraBox).
  video?: BoxTrack
  capture: string[]
  why: string
  // Framing guide drawn over primerImage on the primer card. DECOUPLED from
  // cameraBox/passBox: coords target the guide photo's composition (as seen through
  // the 16:10 cover-crop of .primer-preview-scene), and `text` is plain-language
  // guidance for the installer, NOT a CV token.
  primerBox: DetectionBoxSpec[]
  cameraBox: DetectionBoxSpec[]
  verify: {
    outcomeTitle: string
    outcomeBody: string
    passBox: DetectionBoxSpec[]
  }
  // Unused in this app (the HVAC base carried a nameplate-vs-breaker compare card).
  compare?: CompareSpec
  // Present → a multi-capture checkpoint (unused in storyline v2).
  subShots?: SubShot[]
}

export const PROOF_STEPS: ProofStep[] = [
  {
    id: 'vehicle',
    num: '1',
    label: 'Item at Vehicle',
    basePhoto: '/proof/proof-1-vehicle.jpg',
    primerImage: '/proof/primer/vehicle.jpg',
    video: vehicleTrack as BoxTrack,
    capture: [
      'Carton upright on the cart at the liftgate',
      'Shipping label and all four corners visible',
      'Before the unit leaves the truck',
    ],
    why: 'Condition at the vehicle is the first timestamp in the claims chain. A carton flagged here — before carry-in — separates packaging damage from anything that happens in the home, in either direction.',
    primerBox: [
      { top: '14%', left: '30%', width: '26%', height: '74%', text: 'Whole carton on the cart, corners visible' },
    ],
    cameraBox: [
      { top: '58%', left: '14%', width: '34%', height: '26%', text: 'CORNER_CRUSH · LOGGED', tone: 'flag' },
    ],
    verify: {
      outcomeTitle: 'Carton damage logged at vehicle',
      outcomeBody:
        'Corner crush, lower left — logged at the vehicle before carry-in. Uncarton at the door and record unit condition. Whatever the walk-around shows, the damage is now attributed to packaging, not the crew or the home.',
      passBox: [
        { top: '58%', left: '14%', width: '34%', height: '26%', text: 'CORNER_CRUSH · LOGGED', tone: 'flag' },
      ],
    },
  },
  {
    id: 'condition',
    num: '2',
    label: 'Condition at Threshold',
    basePhoto: '/proof/proof-2-condition.jpg',
    primerImage: '/proof/primer/condition.jpg',
    video: conditionTrack as BoxTrack,
    capture: [
      'Uncartoned unit just inside the entry',
      'One slow circle — whole unit in frame',
      'Before carry-in to the room',
    ],
    why: 'The walk-around answers the carton flag: crushed packaging, intact unit. Damage found after the crew leaves becomes a contested claim — this clip, timestamped before carry-in, settles "was it damaged at handoff?" in either direction.',
    primerBox: [
      { top: '18%', left: '25%', width: '56%', height: '66%', text: 'Circle slowly, keep the whole unit in frame' },
    ],
    cameraBox: [
      { top: '22%', left: '16%', width: '68%', height: '58%', text: 'UNIT_INTACT' },
    ],
    verify: {
      outcomeTitle: 'Unit intact at threshold',
      outcomeBody:
        'Verified — despite the crushed carton flagged at the vehicle, the unit itself looks great out of the box: finish, corners, and edges all clear. The damage stays with the packaging, not the furniture. Condition logged before carry-in.',
      passBox: [
        { top: '22%', left: '16%', width: '68%', height: '58%', text: 'UNIT_INTACT' },
      ],
    },
  },
  {
    id: 'level',
    num: '3',
    label: 'Carcase Level Check',
    basePhoto: '/proof/proof-3-level.jpg',
    primerImage: '/proof/primer/level.jpg',
    video: levelTrack as BoxTrack,
    capture: [
      'Torpedo level on the dresser top',
      'Bubble readable on camera',
      'Levelers adjusted before the shot',
    ],
    why: 'The federal stability test simulates a forward-tilted unit on carpet — an out-of-level case starts partway into its own failure condition, and drawers on a tilted case drift open or drag.',
    primerBox: [
      { top: '40%', left: '27%', width: '46%', height: '24%', text: 'Level on top, bubble toward camera' },
    ],
    cameraBox: [
      { top: '32%', left: '26%', width: '48%', height: '22%', text: 'TOP_LEVEL' },
    ],
    verify: {
      outcomeTitle: 'Carcase level verified',
      outcomeBody:
        'Bubble centered side-to-side on the dresser top after leveler adjustment. The case sits square on the finished floor.',
      passBox: [
        { top: '32%', left: '26%', width: '48%', height: '22%', text: 'TOP_LEVEL' },
      ],
    },
  },
  {
    id: 'anchor',
    num: '4',
    label: 'Anti-Tip Anchor',
    basePhoto: '/proof/proof-4-anchor-pass.jpg',
    primerImage: '/proof/primer/anchor.jpg',
    video: anchorTrack as BoxTrack,
    capture: [
      'Unit pulled forward — gap to the wall visible',
      'Strap from behind the unit to the wall plate',
      'Strap connected and pulled taut',
    ],
    why: 'This dresser ships with a tip-restraint kit under the federal stability standard — and this order includes anchor-to-wall service. The connection is only photographable now, before the unit slides back flush; once pushed back, a correct anchor is invisible from the room. Furniture tip-overs send ~17,800 people to the ER a year; most fatalities are children.',
    primerBox: [
      { top: '12%', left: '42%', width: '36%', height: '66%', text: 'Shoot down the gap — strap and wall plate in frame' },
    ],
    cameraBox: [
      { top: '28%', left: '24%', width: '52%', height: '34%', text: 'TIP_RESTRAINT' },
    ],
    verify: {
      outcomeTitle: 'Anti-tip anchor verified',
      outcomeBody:
        'Brackets fastened to the unit’s top rear edge, strap connected to the wall plate and holding no slack — the tug-test condition. Slide the unit back flush; the mandated restraint is on the wall, not in the drawer.',
      passBox: [
        { top: '28%', left: '24%', width: '52%', height: '34%', text: 'TIP_RESTRAINT' },
      ],
    },
  },
  {
    id: 'placement',
    num: '5',
    label: 'Placement & Function',
    basePhoto: '/proof/proof-5-placement.jpg',
    primerImage: '/proof/primer/placement.jpg',
    video: placementTrack as BoxTrack,
    capture: [
      'Unit flush to the wall in final position',
      'One drawer halfway open',
      'Room context in frame — no packaging anywhere',
    ],
    why: 'One capture proves the last three deliverables at once: room-of-choice placement, drawer function after the carry-in, and a site clear of packaging. It is the closing frame of the record — and the shot that protects the crew that did the work.',
    primerBox: [
      { top: '32%', left: '12%', width: '52%', height: '48%', text: 'Step back — whole unit and room context' },
    ],
    cameraBox: [
      { top: '24%', left: '12%', width: '76%', height: '58%', text: 'PLACEMENT_SET' },
    ],
    verify: {
      outcomeTitle: 'Placement confirmed — function verified',
      outcomeBody:
        'Unit flush to the wall in the room of choice, drawer running free after carry-in, site clear of packaging. Job ready to close.',
      passBox: [
        { top: '26%', left: '16%', width: '68%', height: '54%', text: 'PLACEMENT_SET' },
      ],
    },
  },
]

// The staged failure beat (anchor step, first attempt): the strap was fastened to the unit but
// never attached to the wall — the kit's most common real-world half-install. The retake — the
// PROOF_STEPS[3] pass above — clears it. `video` is the burned fail clip (red box); `photo` is
// the generated fail plate (still fallback).
export const ANCHOR_FAIL: {
  photo: string
  video?: BoxTrack
  cameraBox: DetectionBoxSpec
  failBox: DetectionBoxSpec
  outcomeTitle: string
  outcomeBody: string
} = {
  photo: '/proof/proof-4-anchor-fail.jpg',
  video: anchorFailTrack as BoxTrack,
  cameraBox: {
    top: '30%',
    left: '24%',
    width: '52%',
    height: '32%',
    text: 'TIP_RESTRAINT · UNANCHORED',
  },
  failBox: {
    top: '28%',
    left: '22%',
    width: '56%',
    height: '36%',
    text: 'TIP_RESTRAINT · UNANCHORED',
  },
  outcomeTitle: 'Anti-tip strap is not anchored',
  outcomeBody:
    'The strap is fastened to the unit but hangs free — it was never attached to the wall. An unanchored dresser is exactly the tip-over case the standard exists for. This order includes anchor-to-wall service; the wall plate and screws ship in the kit. Fasten the plate, connect the strap, pull it taut, and recapture before sliding the unit back.',
}
